import { SprintAPI } from './api/sprintApi';
import { PERSONAL_WORKSPACE_ID } from './api/workspaceContext';

const STORAGE_KEY = 'fokus:pending-task-adds';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function storageRoom(workspaceId) {
  if (!workspaceId || workspaceId === PERSONAL_WORKSPACE_ID) return 'personal';
  return workspaceId;
}

function apiWorkspace(workspaceId) {
  if (!workspaceId || workspaceId === PERSONAL_WORKSPACE_ID || workspaceId === 'personal') {
    return null;
  }
  return workspaceId;
}

function entryKey(workspaceId, sprintId) {
  return `${storageRoom(workspaceId)}:${sprintId}`;
}

function readAll() {
  try {
    const raw = window.localStorage?.getItem?.(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  try {
    window.localStorage?.setItem?.(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* quota or private mode — skip */
  }
}

function stripViewMeta(task) {
  if (!task || typeof task !== 'object') return task;
  const {
    sourceCampaign: _sourceCampaign,
    position: _position,
    _originalPosition,
    _campaignOffset,
    ...rest
  } = task;
  return rest;
}

/**
 * Remember missions that must survive a refresh if the PUT is aborted.
 * Flush on the next load merges them into the campaign on the server.
 */
export function queuePendingTaskAdds(workspaceId, sprintId, tasks) {
  if (!sprintId || !Array.isArray(tasks) || tasks.length === 0) return;
  const cleaned = tasks.map(stripViewMeta).filter((t) => t?.id);
  if (cleaned.length === 0) return;

  const all = readAll();
  const key = entryKey(workspaceId, sprintId);
  const prev = all[key]?.tasks || [];
  const byId = new Map(prev.map((t) => [t.id, t]));
  cleaned.forEach((t) => byId.set(t.id, t));
  all[key] = {
    workspaceId: apiWorkspace(workspaceId) || workspaceId || null,
    sprintId,
    tasks: [...byId.values()],
    queuedAt: Date.now(),
  };
  writeAll(all);
}

export function clearPendingTaskAdds(workspaceId, sprintId, savedTasks) {
  const all = readAll();
  const key = entryKey(workspaceId, sprintId);
  const pending = all[key];
  if (!pending) return;

  if (!Array.isArray(savedTasks)) {
    delete all[key];
    writeAll(all);
    return;
  }

  const savedIds = new Set(savedTasks.map((t) => t.id));
  const stillMissing = (pending.tasks || []).filter((t) => t.id && !savedIds.has(t.id));
  if (stillMissing.length === 0) delete all[key];
  else all[key] = { ...pending, tasks: stillMissing };
  writeAll(all);
}

export function mergePendingIntoSprint(workspaceId, sprint) {
  if (!sprint) return sprint;
  const pending = readAll()[entryKey(workspaceId, sprint.id)];
  if (!pending?.tasks?.length) return sprint;
  const existing = Array.isArray(sprint.tasks) ? sprint.tasks : [];
  const ids = new Set(existing.map((t) => t.id));
  const extras = pending.tasks.filter((t) => t.id && !ids.has(t.id));
  if (extras.length === 0) return sprint;
  return { ...sprint, tasks: [...existing, ...extras] };
}

/** Replay queued new missions into the workspace on the server (union, never wipe). */
export async function flushPendingTaskAdds(workspaceId) {
  const all = readAll();
  const now = Date.now();
  let expired = false;
  for (const [key, item] of Object.entries(all)) {
    if (now - (item.queuedAt || 0) > MAX_AGE_MS) {
      delete all[key];
      expired = true;
    }
  }
  if (expired) writeAll(all);

  const room = storageRoom(workspaceId);
  const entries = Object.values(all).filter(
    (item) => item?.sprintId && storageRoom(item.workspaceId) === room
  );

  for (const item of entries) {
    const pin = { workspaceId: apiWorkspace(item.workspaceId) ?? apiWorkspace(workspaceId) };
    try {
      const server = await SprintAPI.get(item.sprintId, pin);
      if (!server) continue;
      const ids = new Set((server.tasks || []).map((t) => t.id));
      const extras = (item.tasks || []).filter((t) => t.id && !ids.has(t.id));
      if (extras.length === 0) {
        clearPendingTaskAdds(item.workspaceId || workspaceId, item.sprintId, server.tasks);
        continue;
      }
      const merged = {
        ...server,
        categories: server.categories || [],
        tasks: [...(server.tasks || []), ...extras],
      };
      await SprintAPI.update(item.sprintId, merged, pin);
      clearPendingTaskAdds(item.workspaceId || workspaceId, item.sprintId, merged.tasks);
    } catch (err) {
      console.error('Failed to flush pending mission:', err);
    }
  }
}
